import {FC, useEffect, useRef, useState} from 'react';
import {FileWithPath} from 'react-dropzone';
import {Controller, FormProvider, useForm, useWatch} from 'react-hook-form';
import {CheckboxElement, TextFieldElement} from 'react-hook-form-mui';
import {yupResolver} from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Chip,
  Grid2 as Grid,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import useUpdateEffect from 'ahooks/es/useUpdateEffect';
import {isAxiosError} from 'axios';
import isNil from 'lodash/isNil';
import {useSnackbar} from 'notistack';
import {boolean, mixed, object, string} from 'yup';

import {
  Category,
  CategorySelect,
  SubCategory,
  SubCategorySelect,
} from '@entities/category';
import {
  GeoFilterOption,
  useGetGeoFilterOptions,
  useUploadAdminFile,
} from '@entities/common';
import {Topic, useUpdateTopic} from '@entities/topic';
import {getBackendAssetPath, getErrorMessageFromApiError} from '@shared/api';
import {
  FieldChangeHighlight as FieldHighlight,
  imageFileTypes,
  ImageUploadField,
  ObjectSelect,
  RhfDateTimePicker,
  TranslateFieldButton,
} from '@shared/components';
import {resolveEnglishText} from '@shared/helpers';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {dummyFunc, toPickerDateInTz} from '@shared/utils';
import {FormRow} from '@widgets/common';
import {openAddTranslationsDialog} from '@widgets/common/AddTranslationsDialog';

type FormValues = {
  name: string;
  shortName: string;
  streamUrl: string;
  category: Category | null;
  subCategory: SubCategory | null;
  geos: GeoFilterOption[];
  logoFile: FileWithPath | null;
  bannerFile: FileWithPath | null;
  startDate: Date | null;
  endDate: Date | null;
  autoPublishEnabled: boolean;
};

const v8nSchema = object().shape({
  name: string().required(),
  shortName: string().required(),
  streamUrl: string().defined(),
  category: mixed<Category>()
    .required()
    .nullable()
    .notOneOf([null], 'required'),
  subCategory: mixed<SubCategory>()
    .required()
    .nullable()
    .notOneOf([null], 'required'),
  geos: mixed<GeoFilterOption[]>().defined().default([]),
  logoFile: mixed<FileWithPath>().nullable().defined(),
  bannerFile: mixed<FileWithPath>().nullable().defined(),
  startDate: mixed<Date>().nullable().defined(),
  endDate: mixed<Date>().nullable().defined(),
  autoPublishEnabled: boolean().required(),
});

interface Props {
  topic: Topic;
  onClose: () => void;
  onAutoPublishChange?: (enabled: boolean) => void;
}

export const EditTopicForm: FC<Props> = ({
  topic,
  onClose,
  onAutoPublishChange,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: uploadAdminFile, isPending: isUploadingFiles} =
    useUploadAdminFile();
  const {mutateAsync: updateTopic, isPending: isUpdatingTopic} =
    useUpdateTopic();
  const {data: geoOptions = []} = useGetGeoFilterOptions();
  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    () => topic.nameTranslations,
  );

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      name: topic.name,
      shortName: topic.shortName,
      streamUrl: topic.streamUrl ?? '',
      category: topic.category ?? null,
      subCategory: topic.subCategories?.[0] ?? null,
      // The saved tags are known by slug only; the label of each chip is
      // resolved from geoOptions on render, which may still be loading.
      geos: topic.geoSlugs.map(slug => ({id: slug, name: slug})),
      logoFile: null,
      bannerFile: null,
      startDate: topic.startDate ? toPickerDateInTz(topic.startDate) : null,
      endDate: topic.endDate ? toPickerDateInTz(topic.endDate) : null,
      autoPublishEnabled: topic.autoPublishEnabled,
    },
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: {dirtyFields},
  } = formMethods;

  const currentName = useWatch({control, name: 'name'});
  const currentAutoPublishEnabled = useWatch({
    control,
    name: 'autoPublishEnabled',
  });

  useEffect(() => {
    onAutoPublishChange?.(currentAutoPublishEnabled);
  }, [currentAutoPublishEnabled, onAutoPublishChange]);

  const currentCategory = useWatch({control, name: 'category'});
  const initialCategoryId = useRef(topic.category?.id);

  useUpdateEffect(() => {
    if (
      !isNil(currentCategory) &&
      currentCategory.id !== initialCategoryId.current
    ) {
      setValue('subCategory', null, {shouldDirty: true});
    }
  }, [currentCategory?.id]);

  const onSubmit = async (values: FormValues) => {
    try {
      const uploadedLogo = values.logoFile
        ? await uploadAdminFile({
            file: values.logoFile,
            type: 'topic_logo',
          })
        : null;

      const uploadedBanner = values.bannerFile
        ? await uploadAdminFile({
            file: values.bannerFile,
            type: 'topic_banner',
          })
        : null;

      await updateTopic({
        topicId: topic.topicId,
        nameTrans: {
          ...nameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        shortName: values.shortName,
        logoUrl: uploadedLogo?.key ?? getBackendAssetPath(topic.logoUrl),
        bannerUrl: uploadedBanner?.key ?? getBackendAssetPath(topic.bannerUrl),
        streamUrl: values.streamUrl.trim() || null,
        subCategorySlugs: [
          values.subCategory?.slug ?? values.subCategory?.id ?? '',
        ],
        startDate: values.startDate ?? toPickerDateInTz(topic.startDate),
        endDate:
          values.endDate ??
          values.startDate ??
          toPickerDateInTz(topic.startDate),
        isLive: topic.isLive,
        isFavorite: topic.isFavorite ?? false,
        geoSlugs: values.geos.map(g => g.id),
        autoPublishEnabled: values.autoPublishEnabled,
      });

      enqueueSnackbar('Topic updated successfully', {
        variant: 'success',
      });
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';

      enqueueSnackbar(`Could not update topic. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormProvider {...formMethods}>
        <Grid container rowSpacing={1} columnSpacing={4} mt={2}>
          <Grid size={6}>
            <Stack spacing={5} mb={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <FieldHighlight changed={!!dirtyFields.name} flex={1}>
                  <TextFieldElement
                    name="name"
                    label="Name"
                    control={control}
                    autoFocus
                  />
                </FieldHighlight>

                <TranslateFieldButton
                  onClick={() => {
                    void openAddTranslationsDialog({
                      fieldName: 'Name',
                      initialTranslations: nameTranslations,
                      enTrans: resolveEnglishText(
                        nameTranslations,
                        currentName,
                        !!dirtyFields.name,
                      ),
                    })
                      .then((translations: TranslationDict) => {
                        setValue(
                          'name',
                          translations[SupportedLanguage.English],
                          {shouldDirty: true},
                        );
                        setNameTranslations(translations);
                        return;
                      })
                      .catch(dummyFunc);
                  }}
                />
              </Box>
              <FieldHighlight changed={!!dirtyFields.shortName}>
                <TextFieldElement
                  name="shortName"
                  label="Short name"
                  control={control}
                />
              </FieldHighlight>
              <FieldHighlight changed={!!dirtyFields.streamUrl}>
                <TextFieldElement
                  name="streamUrl"
                  label="Stream URL"
                  control={control}
                  sx={{width: '100%'}}
                />
              </FieldHighlight>
              <FormRow>
                <FieldHighlight changed={!!dirtyFields.startDate}>
                  <RhfDateTimePicker
                    name="startDate"
                    control={control}
                    label="Start date"
                  />
                </FieldHighlight>
                <FieldHighlight changed={!!dirtyFields.endDate}>
                  <RhfDateTimePicker
                    name="endDate"
                    control={control}
                    label="End date"
                  />
                </FieldHighlight>
              </FormRow>
              <FieldHighlight changed={!!dirtyFields.autoPublishEnabled}>
                <CheckboxElement
                  name="autoPublishEnabled"
                  label="Auto publish"
                  control={control}
                />
              </FieldHighlight>
              {/* <FormRow>
                <Box display="flex" gap={2}>
                  <Typography>Trading volume:</Typography>
                  <Typography>
                    <strong>{topic.tradingVolume}</strong>
                  </Typography>
                </Box>
              </FormRow> */}
            </Stack>
          </Grid>

          <Grid size={6}>
            <Stack spacing={5}>
              <Controller
                name="category"
                control={control}
                render={({field, fieldState}) => (
                  <FieldHighlight changed={!!dirtyFields.category}>
                    <CategorySelect
                      label="Category"
                      error={fieldState.error?.message}
                      {...field}
                    />
                  </FieldHighlight>
                )}
              />

              <Controller
                name="subCategory"
                control={control}
                render={({field, fieldState}) => (
                  <FieldHighlight changed={!!dirtyFields.subCategory}>
                    <SubCategorySelect
                      label="Sub-category"
                      categoryId={currentCategory?.id ?? ''}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  </FieldHighlight>
                )}
              />

              <Controller
                name="geos"
                control={control}
                render={({field}) => {
                  return (
                    <FieldHighlight changed={!!dirtyFields.geos}>
                      <ObjectSelect<GeoFilterOption>
                        label="GEO tags"
                        multiple
                        options={geoOptions}
                        labelProp="name"
                        valueProp="id"
                        value={field.value ?? []}
                        onChange={val => field.onChange(val ?? [])}
                        renderOption={opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {opt.logo_url && (
                                <img
                                  src={opt.logo_url}
                                  alt=""
                                  width="auto"
                                  height={20}
                                  style={{flexShrink: 0}}
                                />
                              )}
                              <Typography>{opt.name || opt.id}</Typography>
                            </Box>
                          </MenuItem>
                        )}
                        renderValue={selected => (
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {(selected as string[]).map(id => {
                              const opt = geoOptions.find(o => o.id === id);
                              return (
                                <Chip
                                  key={id}
                                  size="small"
                                  label={opt ? opt.name || opt.id : id}
                                />
                              );
                            })}
                          </Box>
                        )}
                      />
                    </FieldHighlight>
                  );
                }}
              />
            </Stack>
          </Grid>
        </Grid>

        <FormRow mt={2} mb={6}>
          <Box display="flex" gap={4} alignItems="flex-start" flexWrap="wrap">
            <Stack spacing={1} width={160}>
              <ImageUploadField<FormValues>
                fieldName="logoFile"
                label="Avatar"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={topic.logoUrl}
                width={160}
                height={160}
                alt="Topic avatar"
                highlighted={!!dirtyFields.logoFile}
              />
            </Stack>

            <Stack spacing={1} width={420}>
              <ImageUploadField<FormValues>
                fieldName="bannerFile"
                label="Banner"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={topic.bannerUrl}
                width="100%"
                height={160}
                imageFit="cover"
                alt="Topic banner"
                highlighted={!!dirtyFields.bannerFile}
              />
            </Stack>
          </Box>
        </FormRow>

        <FormRow width={420} mt={2}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
          >
            Back
          </Button>

          <Button
            type="submit"
            sx={{flex: 2}}
            disabled={isUploadingFiles || isUpdatingTopic}
          >
            Apply changes
          </Button>
        </FormRow>
      </FormProvider>
    </form>
  );
};
